// https://stylexjs.com/docs/learn/installation/vite/vite-react/
import './stylex.css'

import { createRoot } from 'react-dom/client'

import * as Sentry from '@sentry/react'
import posthog, { CapturedNetworkRequest } from 'posthog-js'

import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
// NOTE - Should rely on I18n.locale for internationalization
import 'moment/dist/locale/fr'

import { make as Root } from './Root.gen'

import { sentryDsn, intercomAppID, context, postHogAppKey } from './core/env'
import { installHelpCenter } from './core/helpCenter'
import { installDevicePixelRatioVariable } from './core/devicePixelRatio'

installDevicePixelRatioVariable()

const contextValue = context()

const consoleError = console.error
const SUPPRESSED_ERRORS = ['%s cannot appear as a child of <%s>']

console.error =
  contextValue !== 'production'
    ? function (message, ...args) {
      if (!SUPPRESSED_ERRORS.some((error) => message.includes(error))) {
        consoleError(message, ...args)
      }
    }
    : console.error

// Polyfill for InputNumberField.res necessary for Safari browser < v13.0
// see: https://github.com/dm4t2/vue-currency-input/issues/15#issuecomment-618053196
if (!Intl.NumberFormat.prototype.formatToParts) {
  Intl.NumberFormat = require('intl').NumberFormat
  require('intl/locale-data/jsonp/en-US.js')
  require('intl/locale-data/jsonp/fr-FR.js')
}

if (contextValue === 'production') {
  posthog.init(postHogAppKey(), {
    api_host: 'https://eu.i.posthog.com',
    // NOTE — Ensures person profiles are created only for identified users,
    // avoiding unnecessary profiles for anonymous users.
    person_profiles: 'identified_only',
    // NOTE - Requires specific people data attributes configured in the Intercom account
    integrations: { intercom: true, crispChat: false },
    // NOTE — Excludes click/inputs/interaction events from being captured
    autocapture: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: { password: true },
      // NOTE - PostHog doesn't parse operationName from the request payload:
      // below, the request URL (request.name) is changed to the operationName
      // that will be shown in the session recording network tab.
      maskCapturedNetworkRequestFn: (request: CapturedNetworkRequest) => {
        if (request.name.includes('/graphql') && request.requestBody) {
          try {
            const body = JSON.parse(request.requestBody)
            if (body.operationName) {
              request.name = body.operationName
            }
          } catch (e) {
            // noop
          }
        }
        return request
      },
    },
  })
}

installHelpCenter(intercomAppID())

const sentryDsnValue = sentryDsn()

if (
  sentryDsnValue &&
  typeof sentryDsnValue === 'string' &&
  sentryDsnValue.length > 0 &&
  (contextValue === 'staging' || contextValue === 'production')
) {
  Sentry.init({
    dsn: sentryDsnValue,
    environment: contextValue,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    // NOTE - updates Sentry extra data with PostHog session URL timestamped 10s before error
    beforeSend(event) {
      const sessionURL = posthog.get_session_replay_url({
        withTimestamp: true,
        timestampLookBack: 10,
      })
      event.contexts = { ...event.contexts, session: { sessionURL } }
      event.extra = { ...(event.extra ?? {}), sessionURL }
      return event
    },
  })
}

const node = document.getElementById('root')

if (node) {
  createRoot(node).render(<Root />)
}
